/**
 * browserCompat.js — http（非安全上下文）下浏览器不给的 Web API 的统一降级入口
 *
 * ══════════════════════════════════════════════════════════════════
 * 🎯 为什么需要这个文件
 *
 *   教育版（学校机房）当前是 `http://192.168.1.15`，**不是 https 也不是 localhost**，
 *   浏览器判定为「非安全上下文」（window.isSecureContext === false）。
 *   以下 API 在这种页面上被规范标记为 [SecureContext]，直接是 undefined：
 *
 *     crypto.subtle          → 上传前算 SHA-256 去重，一调用就 TypeError
 *     crypto.randomUUID()    → 邀请领取页生成设备 ID，一调用整页白屏
 *     navigator.clipboard    → 所有「复制」按钮
 *
 *   实测踩过：模型 Logo 上传报「❌ 上传失败」，但**请求根本没发出去** ——
 *   nginx access.log 里 check-hash 一条都没有，而不算哈希的品牌 Logo 上传全是 200。
 *   排查方向很容易被带偏到后端/存储上，其实一行都没跑到那儿。
 *
 * ══════════════════════════════════════════════════════════════════
 * ⚠️ 本地复现要用局域网 IP
 *
 *   `http://localhost` 被浏览器**当作安全上下文**，这些 API 照常有，复现不出来。
 *   必须用 `http://<局域网IP>:<port>` 访问才能重现学校站的行为。
 *
 * ══════════════════════════════════════════════════════════════════
 * 📌 学校站以后拿到 https 域名之后
 *
 *   这些回退会自动不再走（原生 API 有了就优先用原生），不需要回来删代码。
 */

/**
 * 计算 ArrayBuffer 的 SHA-256 十六进制串。
 *
 * @param {ArrayBuffer} buffer
 * @returns {Promise<string|null>} 非安全上下文（拿不到 crypto.subtle）时返回 **null**，
 *   而不是抛异常 —— 调用方据此**跳过去重预检、照常上传**。
 *
 * 只降级前端预检：后端 routes/upload.js 用 Node 的 crypto 照常写 file_hashes 表，
 * 哈希记录不断档，等站点上了 https 去重会自动恢复，还能命中这段 http 期间存下的记录。
 */
export async function sha256Hex(buffer) {
  const subtle = window.crypto?.subtle;
  if (!subtle) return null;
  try {
    const hashBuffer = await subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (err) {
    console.warn('[browserCompat] SHA-256 计算失败，跳过去重:', err);
    return null;
  }
}

/**
 * 复制文本到剪贴板。
 *
 * @param {string} text
 * @returns {Promise<boolean>} 是否复制成功（调用方据此决定提示文案）
 *
 * 回退用临时 textarea + document.execCommand('copy')，这是 http 下唯一还能用的路子。
 * execCommand 已废弃但所有主流浏览器仍支持，且**必须在用户手势的调用栈里**才生效
 * —— 所以别把它塞进 setTimeout 或长 await 之后。
 */
export async function copyTextToClipboard(text) {
  if (text == null || text === '') return false;
  const str = String(text);

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(str);
      return true;
    } catch (err) {
      // 权限被拒/文档失焦时继续走下面的回退，不直接判失败
      console.warn('[browserCompat] clipboard.writeText 失败，回退 execCommand:', err);
    }
  }

  try {
    const ta = document.createElement('textarea');
    ta.value = str;
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.opacity = '0';
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, str.length); // iOS Safari 光靠 select() 选不中
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (err) {
    console.warn('[browserCompat] execCommand 复制失败:', err);
    return false;
  }
}

/**
 * 生成 UUID v4。
 *
 * crypto.randomUUID 是 [SecureContext]，http 下没有；
 * 但 **crypto.getRandomValues 不受安全上下文限制**，所以回退依然是密码学强度的随机，
 * 不用退到 Math.random。
 *
 * @returns {string}
 */
export function randomUUID() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  if (window.crypto?.getRandomValues) {
    const b = new Uint8Array(16);
    window.crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40; // version 4
    b[8] = (b[8] & 0x3f) | 0x80; // variant 10x
    const hex = Array.from(b).map(x => x.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // 极端兜底：连 getRandomValues 都没有（老浏览器），此处只用于设备本地标识，不做安全用途
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
